/* WS4 (Free Design Package) — admin six-module UI (B7a).

   Reference: 547.html (main workspace) and 5410 - HTML.html (Feedback Review).
   All six modules are full-width vertical (per §8.1 / §14).  This is a
   minimal admin foundation; the Phase C client Portal WS4 pages are out
   of scope.
*/
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { readErrorDetail } from "../../lib/errors";
import { usePageMeta } from "../../components/Layout";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api`;

function ModuleCard({ n, title, children, testid }) {
    return (
        <section
            data-testid={testid}
            className="rounded-lg border border-[#E5E2DC] bg-white shadow-sm">
            <header className="border-b border-[#E5E2DC] px-4 py-3">
                <h3 className="font-manrope text-[13px] font-semibold uppercase tracking-wide text-[#1D1D1B]">
                    {n}. {title}
                </h3>
            </header>
            <div className="px-4 py-4 text-[14px] text-[#1D1D1B]">
                {children}
            </div>
        </section>
    );
}

function Empty({ label }) {
    return <div className="text-[13px] text-[#888]">{label}</div>;
}

function Pill({ tone = "neutral", children, testid }) {
    const map = {
        neutral: "bg-[#F5F3EE] text-[#1D1D1B]",
        good: "bg-[#E4EFE4] text-[#345E34]",
        warn: "bg-[#FBEEDF] text-[#7A4A22]",
        active: "bg-[#A8543F] text-white",
    };
    return (
        <span
            data-testid={testid}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${map[tone]}`}>
            {children}
        </span>
    );
}

// §Phase 3 — WS4 Portal admin panel moved to the ProjectWorkspace
// right sidebar ("CLIENT MANAGEMENT" → "Client Portal Mgt" button)
// in the Admin UI refactor.  The portal lifecycle actions (create,
// rotate, revoke, reactivate) now live in `ClientPortalMgtDialog`
// inside `ProjectWorkspace.jsx`; identical backend endpoints,
// identical `ws4-portal-*` test ids on the action buttons.


/* ------------------------------------------------------------------ */
/* Compact modal shell — reused for Mark Final/Current and Package    */
/* Summary editor.  No external dep; consistent with the existing     */
/* admin surface's small dialogs.                                      */
/* ------------------------------------------------------------------ */
function ModalShell({ title, onClose, children, testid, wide = false }) {
    return (
        <div
            data-testid={testid}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div
                className={`bg-white rounded-lg shadow-lg border border-[#E5E2DC] w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`}>
                <header className="border-b border-[#E5E2DC] px-4 py-3 flex items-center">
                    <h3 className="font-manrope text-[13px] font-semibold uppercase tracking-wide text-[#1D1D1B]">
                        {title}
                    </h3>
                    <button
                        type="button" onClick={onClose}
                        data-testid={`${testid}-close`}
                        className="ml-auto text-[13px] text-[#666] hover:text-[#1D1D1B]">
                        Close
                    </button>
                </header>
                <div className="px-4 py-4 text-[14px] text-[#1D1D1B]">
                    {children}
                </div>
            </div>
        </div>
    );
}

/* Mark Final/Current — deliberate contextual action per §1A.  Selects
   the exact File version being designated; if the deliverable is
   already Final/Current on a different version, a reason is required
   (backend accepts `reason`). */
function MarkFinalCurrentModal({ projectId, deliverable, onClose, onDone }) {
    const versions = deliverable?.versions || [];
    const currentFvid = deliverable?.final_current_file_version_id || null;
    const [selected, setSelected] = useState(
        currentFvid || (versions.length ? versions[versions.length - 1].file_version_id : "")
    );
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const isChange = Boolean(currentFvid) && selected !== currentFvid;
    const reasonRequired = isChange;
    const submit = async () => {
        setErr("");
        if (!selected) { setErr("Select a File version."); return; }
        if (reasonRequired && !reason.trim()) {
            setErr("A reason is required when changing the current Final / Current designation.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/ws4/deliverables/${deliverable.id}/mark-final-current`,
                { file_version_id: selected,
                    reason: reason.trim() || null },
                { withCredentials: true });
            onDone && await onDone();
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.error || e.message);
        } finally { setBusy(false); }
    };
    return (
        <ModalShell title={`Mark Final / Current — ${deliverable.name}`}
                     onClose={onClose} testid="mark-final-current-modal">
            <div className="text-[12px] text-[#666] mb-3">
                Confirm the exact File version to be designated as the
                Final / Current version for this deliverable. Prior issued
                versions are preserved.
            </div>
            <div className="mb-3" data-testid="mfc-current-label">
                <strong>Current Final / Current:</strong>{" "}
                {currentFvid
                    ? <code className="text-[12px]">{currentFvid.slice(0, 8)}…</code>
                    : <span className="text-[#888]">None</span>}
            </div>
            <label className="block text-[12px] font-medium text-[#1D1D1B] mb-1">
                Version to designate
            </label>
            <select
                data-testid="mfc-version-select"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full border border-[#D5D2CB] rounded px-2 py-1.5 text-[13px] mb-3">
                {versions.length === 0
                    ? <option value="">— no versions attached —</option>
                    : versions.map(v => (
                        <option key={v.file_version_id} value={v.file_version_id}
                                data-testid={`mfc-version-opt-${v.file_version_id}`}>
                            v{v.version_number}
                            {v.file_version_id === currentFvid ? " (current)" : ""}
                        </option>
                    ))}
            </select>
            {reasonRequired && (
                <>
                    <label className="block text-[12px] font-medium text-[#1D1D1B] mb-1">
                        Reason for changing Final / Current (required)
                    </label>
                    <textarea
                        data-testid="mfc-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        className="w-full border border-[#D5D2CB] rounded px-2 py-1.5 text-[13px] mb-3" />
                </>
            )}
            {err && (
                <div data-testid="mfc-error"
                     className="text-[12px] text-red-700 mb-2">{err}</div>
            )}
            <div className="flex justify-end gap-2">
                <button type="button" onClick={onClose}
                        data-testid="mfc-cancel"
                        className="px-3 py-1.5 text-[13px] border border-[#D5D2CB] rounded">
                    Cancel
                </button>
                <button type="button" onClick={submit} disabled={busy || !selected}
                        data-testid="mfc-submit"
                        className="px-3 py-1.5 text-[13px] bg-[#A8543F] text-white rounded disabled:opacity-50">
                    {busy ? "Working…" : "Mark Final / Current"}
                </button>
            </div>
        </ModalShell>
    );
}

/* Package Summary editor — six structured sections + four derived
   read-only values.  Draft / Complete state is authoritative from
   the backend response after every save. */
const SUMMARY_SECTIONS = [
    ["final_scheme_direction", "Final scheme / design direction"],
    ["key_client_decisions", "Key client decisions"],
    ["unresolved_matters", "Unresolved design / technical matters"],
    ["assumptions_limitations", "Assumptions / limitations"],
    ["items_deferred_beyond_free_scope", "Items deferred beyond Free Design scope"],
    ["potential_next_paid_services", "Potential next paid-service needs"],
];

function PackageSummaryModal({ projectId, initial, onClose, onDone }) {
    const [values, setValues] = useState(() => {
        const base = {};
        for (const [k] of SUMMARY_SECTIONS) {
            base[k] = (initial && initial[k]) || "";
        }
        return base;
    });
    const [state, setState] = useState(initial?.state || "draft");
    const [missing, setMissing] = useState(initial?.missing_sections || []);
    const [derived, setDerived] = useState({
        contracted: initial?.derived_contracted_revision_allowance ?? 0,
        used: initial?.derived_revision_rounds_used ?? 0,
        discretionary: initial?.derived_discretionary_rounds_approved ?? 0,
        finals: initial?.derived_final_deliverables ?? [],
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [saved, setSaved] = useState(false);

    const setField = (k, v) => setValues(o => ({ ...o, [k]: v }));
    const insertNoKnown = (k) => setField(k, "None / No known items");

    const save = async () => {
        setErr(""); setSaved(false); setBusy(true);
        try {
            const payload = {};
            for (const [k] of SUMMARY_SECTIONS) {
                payload[k] = (values[k] || "").trim() || null;
            }
            const r = await axios.put(
                `${API}/admin/projects/${projectId}/ws4/package-summary`,
                payload, { withCredentials: true });
            setState(r.data.state);
            setMissing(r.data.missing_sections || []);
            setDerived({
                contracted: r.data.derived_contracted_revision_allowance ?? 0,
                used: r.data.derived_revision_rounds_used ?? 0,
                discretionary: r.data.derived_discretionary_rounds_approved ?? 0,
                finals: r.data.derived_final_deliverables ?? [],
            });
            setSaved(true);
            onDone && await onDone();
        } catch (e) {
            setErr(e?.response?.data?.error || e.message);
        } finally { setBusy(false); }
    };

    return (
        <ModalShell title="Package Summary" onClose={onClose}
                     testid="package-summary-modal" wide>
            <div className="mb-3 flex items-center gap-2">
                <strong>State:</strong>
                <Pill tone={state === "complete" ? "good" : "warn"}
                        testid="pkg-summary-state">
                    {state}
                </Pill>
                {saved && (
                    <span data-testid="pkg-summary-saved"
                             className="text-[12px] text-[#345E34]">Saved.</span>
                )}
            </div>
            <div className="mb-4">
                <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide mb-2">
                    Derived values (read-only)
                </h4>
                <div className="grid grid-cols-2 gap-y-1 text-[13px]"
                        data-testid="pkg-summary-derived">
                    <div>Contracted revision allowance:</div>
                    <div data-testid="pkg-derived-contracted">
                        <strong>{derived.contracted}</strong></div>
                    <div>Revision rounds used:</div>
                    <div data-testid="pkg-derived-used">
                        <strong>{derived.used}</strong></div>
                    <div>Discretionary rounds approved:</div>
                    <div data-testid="pkg-derived-discretionary">
                        <strong>{derived.discretionary}</strong></div>
                    <div>Final / Current deliverables:</div>
                    <div data-testid="pkg-derived-finals">
                        {derived.finals.length === 0
                            ? <span className="text-[#888]">None</span>
                            : <ul className="list-disc pl-5 mb-0">
                                {derived.finals.map(d => (
                                    <li key={d.deliverable_id}>
                                        {d.name}
                                    </li>
                                ))}
                            </ul>}
                    </div>
                </div>
            </div>
            <div className="space-y-3">
                {SUMMARY_SECTIONS.map(([k, label]) => {
                    const isMissing = missing.includes(k);
                    return (
                        <div key={k} data-testid={`pkg-section-${k}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <label className="text-[12px] font-medium text-[#1D1D1B]">
                                    {label}
                                </label>
                                {isMissing && (
                                    <Pill tone="warn"
                                             testid={`pkg-section-missing-${k}`}>
                                        Missing
                                    </Pill>
                                )}
                                <button type="button"
                                        data-testid={`pkg-section-none-${k}`}
                                        onClick={() => insertNoKnown(k)}
                                        className="ml-auto text-[11px] text-[#A8543F] underline">
                                    None / No known items
                                </button>
                            </div>
                            <textarea
                                data-testid={`pkg-section-input-${k}`}
                                value={values[k]}
                                onChange={(e) => setField(k, e.target.value)}
                                rows={2}
                                className="w-full border border-[#D5D2CB] rounded px-2 py-1.5 text-[13px]" />
                        </div>
                    );
                })}
            </div>
            {err && (
                <div data-testid="pkg-summary-error"
                     className="text-[12px] text-red-700 mt-3">{err}</div>
            )}
            <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={onClose}
                        data-testid="pkg-summary-cancel"
                        className="px-3 py-1.5 text-[13px] border border-[#D5D2CB] rounded">
                    Cancel
                </button>
                <button type="button" onClick={save} disabled={busy}
                        data-testid="pkg-summary-save"
                        className="px-3 py-1.5 text-[13px] bg-[#A8543F] text-white rounded disabled:opacity-50">
                    {busy ? "Saving…" : "Save Package Summary"}
                </button>
            </div>
        </ModalShell>
    );
}

export function WS4ActiveModules({ projectId, mode = "active", onCompleted }) {
    const [data, setData] = useState(null);
    const [completion, setCompletion] = useState(null);
    const [err, setErr] = useState("");
    const [mfcDeliv, setMfcDeliv] = useState(null);
    const [pkgOpen, setPkgOpen] = useState(false);
    // Design Issue modals retired 2026-11; state removed.

    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/projects/${projectId}/ws4`,
                { withCredentials: true });
            setData(r.data);
            const c = await axios.get(
                `${API}/admin/projects/${projectId}/ws4/completion`,
                { withCredentials: true });
            setCompletion(c.data);
        } catch (e) {
            setErr(e?.response?.data?.error || e.message);
        }
    }, [projectId]);

    useEffect(() => { refresh(); }, [refresh]);

    if (err) {
        return <div data-testid="ws4-error" className="text-red-700 text-sm">{String(err)}</div>;
    }
    if (!data) {
        return <div data-testid="ws4-loading" className="text-sm text-[#888]">Loading…</div>;
    }

    const rev = data.revision_allowance || {};
    const reqs = data.package_requirements || [];
    // Design Issue + feedback-set data keys retired from the /ws4
    // response payload (2026-11 Design Issue system removal).
    const scope = data.scope_control_items || [];
    const summary = data.package_summary || null;

    // D5 Slice C — coherent mode propagation via fieldset gate.
    const readOnly = mode !== "active";
    return (
        <>
        <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0"
                  data-testid={`ws4-fieldset-mode-${mode}`}>
        {readOnly && (
            <div className="mb-3 px-3 py-2 rounded-md bg-[#F5F1EA] border border-[#E5E2DC] text-[12px] text-[#666]"
                 data-testid={`ws4-mode-banner-${mode}`}>
                {mode === "future"
                    ? "This Workstage is not yet active. Content is shown for reference only."
                    : "This Workstage is completed. Historical data shown, read-only."}
            </div>
        )}
        <div className="flex flex-col gap-4" data-testid="ws4-active-modules">
            <ModuleCard n={1} title="Workstage To-Do / Progress" testid="ws4-module-1-todo">
                <div className="text-[13px] text-[#4a4740] leading-relaxed">
                    Operational To-Dos (Activate Portal · Confirm deliverables · Start design ·
                    Issue initial package · Consolidate feedback · Complete revision rounds ·
                    Issue final package · Complete Package Summary · Set Next Action) are
                    instantiated from the shared A5 / B3 Workstage Task template. This
                    listing shows a summary; live editing lives in the Internal Tasks module.
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="ws4-todo-summary">
                    <div>Contracted revision rounds: <strong data-testid="ws4-todo-contracted">{rev.contracted ?? 0}</strong></div>
                    <div>Rounds used: <strong data-testid="ws4-todo-used">{rev.used ?? 0}</strong></div>
                    <div>Discretionary approved: <strong>{rev.discretionary_approved ?? 0}</strong></div>
                    <div>Remaining: <strong>{rev.remaining ?? 0}</strong></div>
                </div>
            </ModuleCard>

            <ModuleCard n={2} title="Deliverables & Design Issues" testid="ws4-module-2-deliverables">
                {reqs.length === 0
                    ? <Empty label="No deliverables configured yet." />
                    : (
                        <table className="w-full text-[13px]" data-testid="ws4-deliverables-table">
                            <thead className="text-left text-[#666] uppercase tracking-wide text-[11px]">
                                <tr>
                                    <th className="py-1">Name</th>
                                    <th>Category</th>
                                    <th>Scope</th>
                                    <th>Required</th>
                                    <th>Status</th>
                                    <th>Current</th>
                                    <th>Final / Current</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {reqs.map(r => {
                                    const finalFv = r.final_current_file_version_id;
                                    const finalVer = (r.versions || []).find(
                                        v => v.file_version_id === finalFv);
                                    return (
                                    <tr key={r.id} className="border-t border-[#F0EDE7]"
                                        data-testid={`ws4-deliverable-row-${r.id}`}>
                                        <td className="py-1.5">{r.name}</td>
                                        <td>{(r.category || "").replace(/_/g, " ")}</td>
                                        <td>{r.required_for_package_completion ? "Required" : "Optional"}</td>
                                        <td>{r.required_for_package_completion ? "Yes" : "No"}</td>
                                        <td>
                                            <Pill tone={r.status === "final_current" ? "good"
                                                : r.status === "issued" ? "neutral" : "neutral"}
                                                testid={`ws4-deliverable-status-${r.id}`}>
                                                {r.status.replace(/_/g, " ")}
                                            </Pill>
                                        </td>
                                        <td>{r.current_version ? `v${r.current_version.version_number}` : "—"}</td>
                                        <td data-testid={`ws4-deliverable-final-${r.id}`}>
                                            {r.status === "final_current" && finalVer
                                                ? <span>v{finalVer.version_number}</span>
                                                : r.status === "final_current"
                                                    ? <span className="text-[#7A4A22]">designated</span>
                                                    : <span className="text-[#888]">—</span>}
                                        </td>
                                        <td className="text-right">
                                            <button type="button"
                                                onClick={() => setMfcDeliv(r)}
                                                disabled={(r.versions || []).length === 0}
                                                data-testid={`ws4-deliverable-mark-final-${r.id}`}
                                                className="text-[12px] text-[#A8543F] underline disabled:opacity-40 disabled:no-underline">
                                                Mark Final / Current
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                {/* Module 2 (Design Issues) + Module 3 (Revision &
                    Client Feedback) retired 2026-11.  Replacement:
                    Current Design Issue sub-group tag on the Files
                    page (writable by admin from a dropdown directly
                    below the Files toolbar). */}
            </ModuleCard>

            <ModuleCard n={4} title="Programme / Milestones" testid="ws4-module-4-programme">
                <div className="text-[13px] text-[#4a4740]">
                    WS4 uses the shared Project Programme.
                </div>
            </ModuleCard>

            <ModuleCard n={5} title="Scope Control" testid="ws4-module-5-scope">
                {scope.length === 0
                    ? <Empty label="No scope-control classifications recorded." />
                    : (
                        <ul className="mt-2 space-y-1 text-[13px]" data-testid="ws4-scope-control-list">
                            {scope.map(s => (
                                <li key={s.id}
                                    className="flex items-start gap-2"
                                    data-testid={`ws4-scope-row-${s.id}`}>
                                    <Pill tone={s.classification === "proposed_paid_additional" ? "warn" : "neutral"}>
                                        {(s.classification || "").replace(/_/g, " ")}
                                    </Pill>
                                    <span>
                                        <strong>{s.request_text}</strong>
                                        <div className="text-[12px] text-[#666]">Reason: {s.reason}</div>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
            </ModuleCard>

            <ModuleCard n={6} title="Free Design Package Completion" testid="ws4-module-6-completion">
                <div className="mb-3 flex items-center gap-2" data-testid="ws4-package-summary-panel">
                    <strong>Package Summary:</strong>{" "}
                    <Pill tone={summary?.state === "complete" ? "good" : "warn"}
                             testid="ws4-package-summary-state">
                        {summary?.state || "not started"}
                    </Pill>
                    <button type="button"
                        onClick={() => setPkgOpen(true)}
                        data-testid="ws4-package-summary-edit"
                        className="ml-auto text-[12px] text-[#A8543F] underline">
                        View / Edit Package Summary
                    </button>
                </div>
                {summary?.state === "complete" && summary.final_scheme_direction && (
                    <div className="mb-3 text-[13px] text-[#4a4740]"
                            data-testid="ws4-package-summary-preview">
                        <strong>Direction:</strong> {summary.final_scheme_direction}
                    </div>
                )}
                <div className="mt-2" data-testid="ws4-completion-blockers-panel">
                    <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide">
                        Completion blockers
                    </h4>
                    {completion?.can_complete
                        ? <div className="mt-1 text-[13px] text-[#345E34]" data-testid="ws4-can-complete">
                            All completion facts satisfied. WS4 → WS5 transition available.
                        </div>
                        : (
                            <ul className="mt-1 list-disc pl-5 text-[13px] text-[#7A5148]"
                                 data-testid="ws4-blockers-list">
                                {(completion?.blockers || []).map((b, i) => (
                                    <li key={i} data-testid={`ws4-blocker-${b.code}`}>{b.message}</li>
                                ))}
                            </ul>
                        )}
                </div>
            </ModuleCard>
        </div>
        </fieldset>
        {mfcDeliv && (
            <MarkFinalCurrentModal
                projectId={projectId}
                deliverable={mfcDeliv}
                onClose={() => setMfcDeliv(null)}
                onDone={refresh} />
        )}
        {pkgOpen && (
            <PackageSummaryModal
                projectId={projectId}
                initial={summary}
                onClose={() => setPkgOpen(false)}
                onDone={refresh} />
        )}
        {/* Design Issue modals retired 2026-11. */}
        </>
    );
}

export default WS4ActiveModules;



// =====================================================================
// Design Issue system retired 2026-11 (Option A).  Deleted components:
//   DesignIssueCreateModal, DesignIssueEditDueModal,
//   DesignIssueIssueModal, FeedbackReviewPage, ITEM_ACTIONS table.
// Deleted routes (also retired in backend/stage5_b7a_ws4.py):
//   POST   /ws4/design-issues
//   PATCH  /ws4/design-issues/{iid}/feedback-due-date
//   POST   /ws4/design-issues/{iid}/issue
//   POST   /ws4/revisions/start
//   POST   /ws4/feedback-sets/seed-submitted
//   PATCH  /ws4/feedback-items/{iid}
//   POST   /ws4/feedback-items/bulk
//   GET    /ws4/feedback-sets/{fsid}
//   POST   /ws4/feedback-sets/{fsid}/resolve
//   POST   /ws4/feedback-sets/{fsid}/reopen
// Admin surface for marking the Current Design Issue now lives on the
// Files page (B3aPages.jsx) via a sub-group dropdown + emerald badge.
// =====================================================================
